'use client';

import { c as _c } from "react/compiler-runtime";
import { useCouponSettings } from '@svs/nova-poolbetting/core/frameworks/nextjs/coupon';
import { useDraw } from '@svs/nova-poolbetting/core/frameworks/nextjs/draw';
import type { Outcome } from '@svs/nova-poolbetting/schema/coupon';
import type { DrawData, DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { EventNumber } from '@svs/nova-poolbetting/schema/sport';
import { CouponEventOutcomesPrediction } from '@svs/nova-poolbetting/ui/Coupon';
import { useMediaQueryAbove } from '@svs/ui/react/media-query';
import type { ReactNode } from 'react';
import { CouponEventOutcomeClientContainer } from './CouponEventOutcomeClientContainer';
type CouponEventDistributionContainerProps = {
  outcomeCount: number;
  product: Definition['moduleEngine'];
  drawNumber: DrawNumber;
  eventNumber: EventNumber;
  selectedOutcomes?: Outcome[];
};
const getOutcomeDistribution = (drawData: DrawData | undefined, eventNumber: EventNumber): (string | null | undefined)[] | undefined => {
  if (!drawData) {
    return undefined;
  }
  const drawEvent = drawData.draw.events.find(drawEvent => drawEvent.eventNumber === eventNumber);
  const bettingEventId = drawEvent?.status === 'COMPLETE' ? drawEvent.bettingEventId : undefined;
  if (!bettingEventId) {
    return undefined;
  }
  const eventTypeStatisticsId = drawData.bettingEvent[bettingEventId]?.eventTypeStatisticsId;
  if (!eventTypeStatisticsId) {
    return undefined;
  }
  const eventStatistics = drawData.eventTypeStatistics[eventTypeStatisticsId];
  if (!eventStatistics) {
    return undefined;
  }
  return eventStatistics.distribution.current.value;
};

/**
 * Container component for Coupon Event Distribution.
 *
 * @param root0 props for CouponEventDistributionContainer
 * @param root0.outcomeCount Outcome count for the event
 * @param root0.product Product for the draw
 * @param root0.drawNumber Draw number for the draw
 * @param root0.eventNumber Event number for the coupon event
 * @param root0.selectedOutcomes Selected outcomes for the event
 * @returns Coupon event outcome component
 */
export function CouponEventDistributionContainer(t0) {
  const $ = _c(16);
  const {
    outcomeCount,
    product,
    drawNumber,
    eventNumber,
    selectedOutcomes
  } = t0;
  const couponSettings = useCouponSettings(product, "RESULT");
  const isLargeScreen = useMediaQueryAbove("md");
  let t1;
  if ($[0] !== drawNumber || $[1] !== product) {
    t1 = {
      product,
      drawNumber
    };
    $[0] = drawNumber;
    $[1] = product;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const draw = useDraw(t1);
  let localDistribution;
  if (!draw.isError && couponSettings.data.outcomeLabel === "LocalDistribution") {
    let t2;
    if ($[3] !== draw.data || $[4] !== eventNumber) {
      t2 = getOutcomeDistribution(draw.data, eventNumber);
      $[3] = draw.data;
      $[4] = eventNumber;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    localDistribution = t2;
  }
  let t2;
  if ($[6] !== drawNumber || $[7] !== eventNumber || $[8] !== isLargeScreen || $[9] !== product) {
    t2 = props => <CouponEventOutcomeClientContainer {...props} key={props.index} buttonSize={isLargeScreen ? "md" : "sm"} product={product} drawNumber={drawNumber} eventNumber={eventNumber} />;
    $[6] = drawNumber;
    $[7] = eventNumber;
    $[8] = isLargeScreen;
    $[9] = product;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== localDistribution || $[12] !== outcomeCount || $[13] !== selectedOutcomes || $[14] !== t2) {
    t3 = <CouponEventOutcomesPrediction selectedOutcomes={selectedOutcomes} outcomeCount={outcomeCount} localDistribution={localDistribution} renderItem={t2} />;
    $[11] = localDistribution;
    $[12] = outcomeCount;
    $[13] = selectedOutcomes;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}